import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { matchRoutes, useLocation, RouteObject } from 'react-router-dom';
import { brandConfig } from '../brandConfig';
import {
  BRANDING_APPLIED_EVENT,
  DocumentTitleContext,
  DocumentTitleRegistry,
  RouteTitleParts,
  TitleSourceModule,
  deriveRouteTitle,
  formatDocumentTitle,
} from '../hooks/useDocumentTitle';

/**
 * DocumentTitleManager — the ONE writer of `document.title` (2026-09-11).
 *
 * Mount once in App.tsx, around `<Routes>`, passing the RESOLVED module list:
 *
 *   <DocumentTitleManager modules={resolvedModules} staticTitles={STATIC_ROUTE_TITLES}>
 *     <Routes>…</Routes>
 *   </DocumentTitleManager>
 *
 * Every tab then reads `<Page> | <Site>` with no per-module code — the module
 * is never its own segment (Tom, 2026-09-11):
 *   1. the URL is matched against every module route (plus `staticTitles` for
 *      App.tsx's hand-written routes) with react-router's own ranking, so the
 *      title agrees with the route `<Routes>` rendered;
 *   2. the matched route's page name comes from `deriveRouteTitle()` — nav
 *      label, else the path ("/employees/new" → "New Employee"), else the
 *      module's own name on its root route;
 *   3. a page's `usePageTitle("Jane Smith")` replaces the page name.
 * `formatDocumentTitle()` reads a bare generic name with its module
 * ("Dashboard" → "Admissions Dashboard") so twelve tabs never all say "Dashboard".
 *
 * When to write:
 *   - on every navigation and every page override → always;
 *   - on the tenant-branding event → only if nobody else has written the title
 *     since our last write. That is what lets react-helmet pages (SEOHead, vlog,
 *     state-of-play) keep the title they set: we never re-stamp over them when
 *     the brand lands late.
 *
 * Standard: vault/how-to/how-to-build-new-module.md § "Browser tab titles".
 */

interface DocumentTitleManagerProps {
  /** The resolved module list — anything with a name, nav and route paths. */
  modules: ReadonlyArray<TitleSourceModule>;
  /** App.tsx's hand-written routes: path pattern → <Page> part ("" = site name only). */
  staticTitles?: Record<string, string>;
  children?: React.ReactNode;
}

const NO_STATIC_TITLES: Record<string, string> = {};

export const DocumentTitleManager: React.FC<DocumentTitleManagerProps> = ({
  modules,
  staticTitles = NO_STATIC_TITLES,
  children,
}) => {
  const { pathname } = useLocation();

  // Static entries first: on an exact tie react-router keeps declaration order,
  // and a hand-written route is declared on purpose.
  const routeTable = useMemo<RouteObject[]>(() => {
    const table: RouteObject[] = Object.entries(staticTitles).map(([path, page]) => ({
      path,
      handle: { page: page || undefined } as RouteTitleParts,
    }));
    for (const moduleConfig of modules) {
      for (const route of moduleConfig.routes ?? []) {
        if (route?.path) table.push({ path: route.path, handle: deriveRouteTitle(moduleConfig, route.path) });
      }
    }
    return table;
  }, [modules, staticTitles]);

  const derived = useMemo<RouteTitleParts | undefined>(
    () => matchRoutes(routeTable, pathname)?.[0]?.route.handle as RouteTitleParts | undefined,
    [routeTable, pathname]
  );

  // Read at write time, so an override registered during this commit already
  // pairs with THIS route's module part.
  const derivedRef = useRef(derived);
  derivedRef.current = derived;

  const overrides = useRef(new Map<object, string>());
  const lastWritten = useRef<string | null>(null);

  const write = useCallback((force: boolean) => {
    if (!force && lastWritten.current !== null && document.title !== lastWritten.current) return;
    const pageOverrides = Array.from(overrides.current.values());
    const override = pageOverrides[pageOverrides.length - 1];
    const parts = derivedRef.current;
    const next = formatDocumentTitle({
      page: override ?? parts?.page,
      module: parts?.module,
      qualifier: parts?.qualifier,
      site: brandConfig.siteName,
    });
    lastWritten.current = next;
    if (document.title !== next) document.title = next;
  }, []);

  const registry = useMemo<DocumentTitleRegistry>(
    () => ({
      set: (key, pageTitle) => {
        overrides.current.delete(key); // re-insert so the most recent caller wins
        overrides.current.set(key, pageTitle);
        write(true);
      },
      clear: (key) => {
        if (overrides.current.delete(key)) write(true);
      },
    }),
    [write]
  );

  useEffect(() => {
    write(true);
  }, [derived, pathname, write]);

  useEffect(() => {
    const onBrandingApplied = () => write(false);
    window.addEventListener(BRANDING_APPLIED_EVENT, onBrandingApplied);
    return () => window.removeEventListener(BRANDING_APPLIED_EVENT, onBrandingApplied);
  }, [write]);

  return <DocumentTitleContext.Provider value={registry}>{children}</DocumentTitleContext.Provider>;
};

export default DocumentTitleManager;
